export type PlaceCategory = 'municipal' | 'sport' | 'culture' | 'parc' | 'ecocentre' | 'piles' | 'dons'

export interface Place {
  id: string
  name: string
  category: PlaceCategory
  address: string
  note: string
  open: boolean
  lat: number
  lng: number
}

export const placeCategories: Record<PlaceCategory, { label: string; color: string }> = {
  municipal: { label: 'Services municipaux', color: '#16265c' },
  sport: { label: 'Sports et loisirs', color: '#ec9522' },
  culture: { label: 'Culture', color: '#2f5bc9' },
  parc: { label: 'Parcs', color: '#5e9c2d' },
  ecocentre: { label: 'Écocentres', color: '#0f766e' },
  piles: { label: 'Piles et batteries', color: '#c2410c' },
  dons: { label: 'Dons et récupération', color: '#7c3aed' },
}

export const cityCenter: [number, number] = [45.4303, -73.8639]

/**
 * Les noms de parcs, les adresses et les équipements sont relevés sur beaconsfield.ca.
 * Les COORDONNÉES, elles, sont approximatives : elles placent chaque lieu dans le bon
 * quartier, mais n'ont pas été géocodées. À affiner avant une démo où la carte compte.
 */
export const places: Place[] = [
  {
    id: 'hotel-de-ville',
    name: 'Hôtel de ville de Beaconsfield',
    category: 'municipal',
    address: '303, boulevard Beaconsfield',
    note: 'Ouvre lundi 8 h',
    open: false,
    lat: 45.4312,
    lng: -73.8625,
  },
  {
    id: 'bibliotheque',
    name: 'Bibliothèque de Beaconsfield',
    category: 'culture',
    address: '303, boulevard Beaconsfield',
    note: 'Ferme à 20 h',
    open: true,
    lat: 45.4314,
    lng: -73.8631,
  },
  {
    id: 'centre-culturel',
    name: 'Centre culturel Beaconsfield',
    category: 'culture',
    address: '288, boulevard Beaconsfield',
    note: 'Expositions et spectacles',
    open: true,
    lat: 45.43,
    lng: -73.864,
  },
  {
    id: 'centre-recreatif',
    name: 'Centre récréatif de Beaconsfield',
    category: 'sport',
    address: '1974, City Lane',
    note: 'Ferme à 21 h',
    open: true,
    lat: 45.4365,
    lng: -73.857,
  },
  {
    id: 'travaux-publics',
    name: 'Travaux publics et site de dépôt',
    category: 'ecocentre',
    address: '300, chemin Beaurepaire',
    note: 'Lun-ven 7 h 15 à 15 h · samedi d’avril à novembre',
    open: true,
    lat: 45.428,
    lng: -73.87,
  },
  {
    id: 'ecocentre-lasalle',
    name: 'Écocentre LaSalle — résidus dangereux',
    category: 'ecocentre',
    address: 'Agglomération de Montréal · hors Beaconsfield',
    note: 'Peintures, solvants, huiles',
    open: true,
    lat: 45.4215,
    lng: -73.8455,
  },

  // Parcs : noms, adresses et équipements relevés sur le site de la Ville.
  {
    id: 'parc-centennial',
    name: 'Parc Centennial',
    category: 'parc',
    address: '288, boulevard Beaconsfield',
    note: 'Aire de jeux et accès à l’eau',
    open: true,
    lat: 45.4298,
    lng: -73.8644,
  },
  {
    id: 'parc-christmas',
    name: 'Parc Christmas',
    category: 'parc',
    address: '422, boulevard Beaconsfield',
    note: 'Chalet, aire de jeux et terrains sportifs',
    open: true,
    lat: 45.429,
    lng: -73.868,
  },
  {
    id: 'parc-windermere',
    name: 'Parc Windermere',
    category: 'parc',
    address: '303, rue Sherbrooke',
    note: 'Chalet et nombreux terrains sportifs',
    open: true,
    lat: 45.434,
    lng: -73.86,
  },
  {
    id: 'parc-shannon',
    name: 'Parc Shannon',
    category: 'parc',
    address: '340, Preston Drive',
    note: 'Chalet, aire de jeux et terrain de soccer',
    open: true,
    lat: 45.435,
    lng: -73.869,
  },
  {
    id: 'parc-rockhill',
    name: 'Parc Rockhill',
    category: 'parc',
    address: '538, Beaurepaire Drive',
    note: 'Chalet, aire de jeux et soccer',
    open: true,
    lat: 45.427,
    lng: -73.872,
  },
  {
    id: 'parc-beacon-hill',
    name: 'Parc Beacon Hill',
    category: 'parc',
    address: '100, Harwood Gate',
    note: 'Patinoire et glissade en hiver',
    open: true,
    lat: 45.438,
    lng: -73.852,
  },
  {
    id: 'parc-city-lane',
    name: 'Parc City Lane',
    category: 'parc',
    address: '15, City Lane',
    note: 'Planchodrome et station de réparation de vélos',
    open: true,
    lat: 45.436,
    lng: -73.858,
  },
  {
    id: 'parc-briarwood',
    name: 'Parc Briarwood',
    category: 'parc',
    address: '50, avenue Willowbrook',
    note: 'Jeux d’eau et terrains sportifs',
    open: true,
    lat: 45.4345,
    lng: -73.866,
  },
  {
    id: 'parc-james-armstrong',
    name: 'Parc James-Armstrong',
    category: 'parc',
    address: '450, chemin Lakeshore',
    note: 'Pavillon accessible et aire ombragée',
    open: true,
    lat: 45.4245,
    lng: -73.869,
  },
  {
    id: 'parc-montrose',
    name: 'Parc Montrose',
    category: 'parc',
    address: 'Avenue Elm',
    note: 'Terrain de soccer et glissade',
    open: true,
    lat: 45.4325,
    lng: -73.876,
  },
  {
    id: 'parc-chiens',
    name: 'Parc à chiens',
    category: 'parc',
    address: 'Rue Elm, à l’ouest de la sortie Woodland',
    note: 'Deux aires séparées selon la taille',
    open: true,
    lat: 45.432,
    lng: -73.877,
  },
  {
    id: 'parc-taywood',
    name: 'Parc Taywood',
    category: 'parc',
    address: '130, Taywood Drive',
    note: 'Aire de jeux et terrain de basketball',
    open: true,
    lat: 45.437,
    lng: -73.862,
  },

  // Points de dépôt de piles et batteries (jamais dans le bac bleu).
  {
    id: 'piles-bibliotheque',
    name: 'Boîte à piles — Bibliothèque',
    category: 'piles',
    address: '303, boulevard Beaconsfield',
    note: 'Piles domestiques, près du comptoir de prêt',
    open: true,
    lat: 45.4316,
    lng: -73.8628,
  },
  {
    id: 'piles-hotel-de-ville',
    name: 'Boîte à piles — Hôtel de ville',
    category: 'piles',
    address: '303, boulevard Beaconsfield',
    note: 'Piles et petites batteries, hall d’entrée',
    open: false,
    lat: 45.431,
    lng: -73.8622,
  },
  {
    id: 'piles-travaux-publics',
    name: 'Travaux publics — batteries',
    category: 'piles',
    address: '300, chemin Beaurepaire',
    note: 'Toutes batteries, y compris auto et vélo électrique',
    open: true,
    lat: 45.4283,
    lng: -73.8705,
  },
  {
    id: 'piles-centre-recreatif',
    name: 'Boîte à piles — Centre récréatif',
    category: 'piles',
    address: '1974, City Lane',
    note: 'Piles domestiques · ferme à 21 h',
    open: true,
    lat: 45.4368,
    lng: -73.8574,
  },

  // Dons de vêtements, récupérateurs et ressourceries.
  {
    id: 'dons-vetements',
    name: 'Boîte de dons de vêtements — Renaissance',
    category: 'dons',
    address: 'Stationnement du Centre récréatif, City Lane',
    note: 'Vêtements, chaussures et textiles, en sacs fermés',
    open: true,
    lat: 45.4362,
    lng: -73.8566,
  },
  {
    id: 'dons-electroniques',
    name: 'Récupération d’électroniques — ARPE',
    category: 'dons',
    address: '300, chemin Beaurepaire',
    note: 'Ordinateurs, téléphones, téléviseurs',
    open: true,
    lat: 45.4277,
    lng: -73.8696,
  },
  {
    id: 'dons-livres',
    name: 'Croque-livres du parc Centennial',
    category: 'dons',
    address: '288, boulevard Beaconsfield',
    note: 'Livres jeunesse, à prendre ou à donner',
    open: true,
    lat: 45.4295,
    lng: -73.8648,
  },
]

/** Lieux où déposer piles et batteries, pour la réponse d'ATNA. */
export const batteryDropOffs = places.filter((place) => place.category === 'piles')
